import React, { useState } from "react";
import Link from "next/link";
import Head from "next/head";
import type { GetStaticProps, NextPage } from "next";

import { Service } from "@models/Service";
import PageLayoutV1 from "@app/hocs/PageLayoutV1";
import * as CmsHttp from "@app/http/cms";
import { META_DESCRIPTION, META_URL, REVALIDATE_SECONDS } from "@app/utilities/constants";
import SimpleCollapsibleHoc from "@components/hocs/SimpleCollapsibleHoc";
import { useEffect } from "react";
import { useRouter } from "next/router";

const mapServices = (services: Service[]): Record<string, Service[]> => {
  const result: Record<string, Service[]> = {};

  if (services && Array.isArray(services) && services.length) {
    for (let i = 0, len = services.length; i < len; i++) {
      const service = services[i],
        type = service.type;

      if (!Object.prototype.hasOwnProperty.call(result, type)) {
        result[type] = [];
      }

      result[type].push(service);
    }
  }

  return result;
};
export type ServicesPageProps = {
  ssrServices: Service[];
};

const ServicesPage: NextPage<ServicesPageProps> = (props) => {
  const { ssrServices } = props,
    router = useRouter(),
    collapsibleCategory = Array.isArray(router.query?.ctg) ? router.query?.ctg[0] : router.query?.ctg,
    serviceRecords: Record<string, Service[]> = mapServices(ssrServices),
    servicesTypes = Object.keys(serviceRecords) || [],
    [expandedCollapsibles, setExpandedCollapsibles] = useState<boolean[]>(servicesTypes.map((svcCtg) => svcCtg.toUpperCase() === collapsibleCategory?.toUpperCase()));

  const onCollapsibleToggled = (id: string, isExpanded: boolean) => {
    if (isExpanded) {
      const tmpExpandedCollapsibles = [...expandedCollapsibles],
        idx = parseInt(id);

      for (let i = 0, len = tmpExpandedCollapsibles.length; i < len; i++) {
        if (i !== idx && tmpExpandedCollapsibles[i] === true) {
          tmpExpandedCollapsibles[i] = false;
        } else if (i === idx) {
          tmpExpandedCollapsibles[i] = true;
        }
      }

      setExpandedCollapsibles(tmpExpandedCollapsibles);
    }
  };

  useEffect(() => {
    if (collapsibleCategory && typeof window !== "undefined") {
      const timer = window.setTimeout(() => {
        const elemToScroll = document.getElementById(collapsibleCategory.toLowerCase());

        if (elemToScroll) {
          elemToScroll.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "start",
          });
        }
      }, 250);

      return () => {
        if (timer) {
          window.clearTimeout(timer);
        }
      };
    }
  }, []);

  /**
   * Meta contents
   */
  const metaTitle = `Services | Heritage Counseling`,
    metaDescription = META_DESCRIPTION || "",
    metaUrl = `${META_URL}/services`,
    metaImage = `${META_URL}/logo.png`;

  return (
    <PageLayoutV1 title="Services">
      <Head>
        <title>{metaTitle}</title>
        <meta key="title" name="title" content={metaTitle} />
        <meta key="description" name="description" content={metaDescription} />

        <meta key="og:title" property="og:title" content={metaTitle} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        <meta key="og:url" property="og:url" content={metaUrl} />
        <meta key="og:image" property="og:image" content={metaImage} />

        <meta key="twitter:url" name="twitter:url" content={metaUrl} />
        <meta key="twitter:title" name="twitter:title" content={metaTitle} />
        <meta key="twitter:description" name="twitter:description" content={metaDescription} />
        <meta key="twitter:image" name="twitter:image" content={metaImage} />

        <meta key="prop:title" itemProp="title" content={metaTitle} />
        <meta key="prop:description" itemProp="description" content={metaDescription} />

        <link rel="canonical" href={metaUrl} />
      </Head>
      <div className="service-page" data-testid="service-page">
        <div className="services-wrapper">
          {servicesTypes.length ? (
            servicesTypes.map((svcCtg, idx) => {
              const services = serviceRecords[svcCtg] || [];

              return (
                <section id={svcCtg.toLowerCase()} key={svcCtg} className="service">
                  <SimpleCollapsibleHoc id={idx.toString()} open={expandedCollapsibles[idx]} title={svcCtg} onToggled={onCollapsibleToggled}>
                    <div className="auto-grid service-links">
                      {services.map((svc) => (
                        <Link key={svc.slug} href={`/services/${svc.typeSlug}/${svc.slug}`}>
                          <a className="link">{svc.name}</a>
                        </Link>
                      ))}
                    </div>
                  </SimpleCollapsibleHoc>
                </section>
              );
            })
          ) : (
            <p className="text-center">No services found</p>
          )}
        </div>
      </div>
    </PageLayoutV1>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const services = await CmsHttp.getServices();

  return {
    props: {
      ssrServices: services,
    } as ServicesPageProps,
    notFound: !services.length,
    revalidate: REVALIDATE_SECONDS, // In seconds
  };
};

export default ServicesPage;
